import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAppDispatch } from 'app/hooks';
import { setBoardMode } from 'app/slices/ahuBoardSlice';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { COMPONENT_FORM_CONFIGS } from '../forms/componentFormConfigs';
import { ACCESSORY_FORM_CONFIGS } from '../forms/accessory/accessoryFormConfigs';
import type { CatalogueFieldConfig } from '../forms/componentFormTypes';
import { truncDec } from '../forms/formKit';

/**
 * Flip-side smart list — the legacy `CatalogueSelector` singleton
 * (`view/base_classes/catalogue_selector.js` + `tmpl/catalogue_selector.tmpl`
 * + `tmpl/catalogue_selector_row.tmpl`), driven by the
 * `rotate:canvas:<kind>:table` channels (sender = CatalogueFieldEngine
 * .toggleSmartList): each open stores the owner id, renders the rows and flips
 * `.main-rotation.rotate`; re-trigger of the same table flips back; row click
 * emits `choose:<x>:master {id, component_id|accessory_id}` + flips back;
 * `rotate:canvas` / CLOSE flip back.
 *
 * ONE surface for every catalogue field. `catalogue_select_field.js:574` binds
 * `button.btn-sync[name=<componentName>]` for accessories AND components alike
 * (the silencer component form's Smartlist is that button —
 * `components/silencer/tmpl/form.tmpl:4-6`), and `rotateOpen`
 * (`catalogue_selector.js:218-227`) keys the whole surface off `componentName`
 * alone: unhide `.only-for-<componentName>`, hide `.spn-row` for vfd/silencer,
 * `min-width-1000` when a column set exists, `min-width-1250` for enclosure.
 *
 * The SPN-only `view/component.table.js` this file first ported is DEAD on
 * legacy — its 22 listeners are `rotate:canvas:<kind>:table` and the only
 * trigger of that shape in the whole tree is `accessories/ams/view/form.js:145`
 * firing `rotate:canvas:ams:table`, a channel it does not listen for. QA batch
 * 8 #1/#2 replaced it for accessory senders only; component senders (silencer,
 * filter, frame, eliminator, evap-pack pump, …) kept the dead markup until now.
 */

/**
 * Every catalogue field's channel. The legacy CatalogueSelector is ONE view
 * bound to every `button.btn-sync`, so it has no listener list to port — this
 * is the same set, derived from the specs the engines are built from rather
 * than hand-kept. The dead component.table.js listened to 22 names, which is
 * why `frame` and `pump` (filter frame, evap-pack pump) opened onto nothing.
 */
const TABLE_KINDS: readonly string[] = (() => {
  const kinds = new Set<string>();
  const add = (fields?: CatalogueFieldConfig[]) =>
    fields?.forEach((f) => kinds.add(f.componentName));
  Object.values(COMPONENT_FORM_CONFIGS).forEach((c) => add(c.catalogueFields));
  Object.values(ACCESSORY_FORM_CONFIGS).forEach((c) => {
    add(c.catalogueFields);
    add(c.catalogueFieldsIntake);
  });
  // `motor:base` / the sheave + starter-child collections reach CompTable
  // under an options.name of their own (NAME_TO_CHANNEL), not a config name.
  ['motor:base', 'sheave', 'belt'].forEach((k) => kinds.add(k));
  return [...kinds];
})();

/**
 * The accessory half of TABLE_KINDS. Legacy marks the owning Data Bar row only
 * for accessories — `const label = !self.view_el.hasClass('component') ? ...`
 * (catalogue_select_field.js:590) — and the engine is shared between the two,
 * so the channel is what tells them apart on this side.
 */
const ACCESSORY_KINDS: ReadonlySet<string> = (() => {
  const kinds = new Set<string>();
  const add = (fields?: CatalogueFieldConfig[]) =>
    fields?.forEach((f) => kinds.add(f.componentName));
  Object.values(ACCESSORY_FORM_CONFIGS).forEach((c) => {
    add(c.catalogueFields);
    add(c.catalogueFieldsIntake);
  });
  return kinds;
})();

/**
 * `label.open` on the Data Bar row whose Smartlist is showing. Legacy resolves
 * the `<li>` around the clicked `.btn-sync` and hands its `> label` to
 * `toggleRotate`, which adds `open` on the OPENING half only
 * (catalogue_select_field.js:589-604 → catalogue_selector.js:204); `rotateBack`
 * clears it (:265). `ahu/styles.css:4698-4701` and `:5146` paint that label
 * `#ebebeb` against the row's white, and it is the only cue on the board saying
 * which accessory the list on the right belongs to. React cleared the class in
 * rotateBack but nothing ever set it, so no row ever went dark.
 *
 * The owner id needs no new plumbing: every catalogue field emits its form
 * model's id as `component_id` (CatalogueFieldEngine.toggleSmartList — the
 * engine is shared, so "component" there just means the row that owns the
 * field), which for an accessory field IS the accessory row id, i.e. the
 * `#forms_accessory_<id>` of TabAccessories. A child collection (AccessDoor's
 * viewport, Light's switch) reports its parent accessory's id — the same row
 * legacy marks, its `<li>` being the nearest one above the button.
 */
const markOwnerRow = (kind: string, ownerId: number | null) => {
  // `$('label.open').removeClass('open')` on every btn-sync click (:591).
  document.querySelectorAll('label.open').forEach((el) => el.classList.remove('open'));
  if (ownerId == null || !ACCESSORY_KINDS.has(kind)) return;
  document.querySelector(`#forms_accessory_${ownerId} > label`)?.classList.add('open');
};

/**
 * NO channel renaming — the pick goes back on the sender's own name.
 *
 * Legacy has no channel at all here: the CatalogueSelector holds a reference to
 * the field that opened it and calls `self.owner.selectModelWithID(...)` on the
 * row click (catalogue_selector.js:60). React models that hand-back as
 * `choose:<componentName>:master`, and the only listener is the engine that sent
 * the list (CatalogueFieldEngine ctor). So the channel IS the sender's
 * `options.name`, which is its `componentName`.
 *
 * This used to run through a NAME_TO_CHANNEL map lifted from the SPN-only
 * `view/component.table.js` — a view that is DEAD on legacy (see the header
 * note), whose 22 `choose:` triggers are named after the *accessory* rather than
 * the field: `choose:starter:contactor:master` for componentName `contactor`.
 * Those two names survived the 6e switch to the real CatalogueSelector, so every
 * Starter Smartlist pick was emitted on a channel nobody listens to and the
 * Contactor / Overload model silently stayed unset.
 */
const channelFor = (name: string, kind: string): string => name || kind;

interface TableRow {
  id: number | string;
  spn?: string | null;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// The 13 legacy `only-for-*` column sets (catalogue_selector.tmpl:6-94 headings
// / catalogue_selector_row.tmpl:8-126 cells), mirrored verbatim and keyed on
// componentName. Kinds without a set (motor, coil, gas_heater, energy_wheel,
// switch, viewport, contactor, overload, uvc parts, …) render SPN only — that
// IS legacy parity: rotateOpen unhides `.only-for-<componentName>` and nothing
// else, so a name with no `<li>` in the template gets no columns.
// ---------------------------------------------------------------------------

/** Handlebars `{{v}}` — null/undefined render empty. */
const s = (v: unknown): string => (v == null ? '' : String(v));

/** renderRow: `if (data.aw_width) data.aw_width = Number(...).toFixed(2)`. */
const fixed2 = (v: unknown): string => (v ? Number(v).toFixed(2) : s(v));

/** `{{width}} x {{height}} x {{depth}}` composite cell. */
const whd = (row: TableRow): string => `${s(row.width)} x ${s(row.height)} x ${s(row.depth)}`;

/**
 * vfd DESCRIPTION cell — legacy renderRow wires the body-level fixed tooltip
 * only when `.col-truncate` overflows (catalogue_selector.js:169-197).
 */
const DescCell = ({ text }: { text: string }) => {
  const truncateRef = useRef<HTMLSpanElement>(null);
  const show = (e: React.MouseEvent<HTMLSpanElement>) => {
    const el = truncateRef.current;
    if (!el || !text || el.scrollWidth <= el.clientWidth) return;
    let tip = document.getElementById('col-desc-fixed-tooltip');
    if (!tip) {
      tip = document.createElement('div');
      tip.id = 'col-desc-fixed-tooltip';
      document.body.appendChild(tip);
    }
    const rect = e.currentTarget.getBoundingClientRect();
    tip.textContent = text;
    tip.style.left = `${rect.left}px`;
    tip.style.top = `${rect.bottom}px`;
    tip.style.display = 'block';
  };
  const hide = () => {
    const tip = document.getElementById('col-desc-fixed-tooltip');
    if (tip) tip.style.display = 'none';
  };
  return (
    <span className="ml-40 w-220 col-desc-wrapper" onMouseEnter={show} onMouseLeave={hide}>
      <span className="col-truncate" ref={truncateRef}>
        {text}
      </span>
    </span>
  );
};

/** catalogue_selector.tmpl heading sets (`.only-for-<kind>.custom-columns`). */
const CATALOGUE_HEADINGS: Record<string, ReactNode> = {
  vfd: (
    <>
      <h4 className="w-160">MANUFACTURER</h4>
      <h4 className="w-150">SERIES</h4>
      <h4 className="w-200">MODEL</h4>
      <h4 className="ml-40 w-220">DESCRIPTION</h4>
      <h4 className="w-120 text-align-right">POWER (HP)</h4>
      <h4 className="w-110 text-align-right">VOLTAGE</h4>
      <h4 className="w-130 text-align-right">
        FLA <span style={{ textTransform: 'none' }}>(Amp)</span>
      </h4>
      <h4 className="ml-50 w-170">NEMA</h4>
    </>
  ),
  damper: (
    <>
      <h4 className="w-200">MANUFACTURER</h4>
      <h4 className="w-100">SERIES</h4>
      <h4 className="w-80 text-align-right">WIDTH</h4>
      <h4 className="w-80 text-align-right">HEIGHT</h4>
      <h4 className="ml-40">BLADES</h4>
    </>
  ),
  louver: (
    <>
      <h4 className="w-200">MANUFACTURER</h4>
      <h4 className="w-100">SERIES</h4>
      <h4 className="w-80 text-align-right">WIDTH</h4>
      <h4 className="w-80 text-align-right">HEIGHT</h4>
    </>
  ),
  adp_gage: (
    <>
      <h4 className="w-200">MANUFACTURER</h4>
      <h4 className="w-200">SERIES</h4>
      <h4 className="w-100">MODEL</h4>
    </>
  ),
  filter: (
    <>
      <h4 className="w-150">MANUFACTURER</h4>
      <h4 className="w-150">SERIES</h4>
      <h4 className="w-150">TYPE</h4>
      <h4 className="w-50 text-align-right">MERV</h4>
      <h4 className="w-80 text-align-right">DEPTH</h4>
    </>
  ),
  frame: (
    <>
      <h4 className="w-200">MANUFACTURER</h4>
      <h4 className="w-200">SERIES</h4>
      <h4 className="w-50">MATERIAL</h4>
    </>
  ),
  eliminator: (
    <>
      <h4 className="w-200">MANUFACTURER</h4>
      <h4 className="w-200">SERIES</h4>
      <h4 className="w-100">TYPE</h4>
      <h4 className="w-80 text-align-right">DEPTH</h4>
    </>
  ),
  light: (
    <>
      <h4 className="w-200">MANUFACTURER</h4>
      <h4 className="w-200">SERIES</h4>
      <h4 className="w-100">STYLE</h4>
      <h4 className="w-80 text-align-right">WATTAGE</h4>
    </>
  ),
  enclosure: (
    <>
      <h4 className="w-140">MANUFACTURER</h4>
      <h4 className="w-70">SERIES</h4>
      <h4 className="w-310">MODEL</h4>
      <h4 className="w-70">NEMA</h4>
      <h4 className="w-100">
        W x H x D <span>(in)</span>
      </h4>
    </>
  ),
  jbox: (
    <>
      <h4 className="w-150">MANUFACTURER</h4>
      <h4 className="w-270">MODEL</h4>
      <h4 className="w-100">NEMA</h4>
      <h4 className="w-100">
        W x H x D <span>(in)</span>
      </h4>
    </>
  ),
  disconnect: (
    <>
      <h4 className="w-140">MANUFACTURER</h4>
      <h4 className="w-80 hide">SERIES</h4>
      <h4 className="w-220">MODEL</h4>
      <h4 className="w-100 pr-30 text-align-right">
        RATING <span>(Amp)</span>
      </h4>
      <h4 className="w-80">NEMA</h4>
      <h4 className="w-100">
        W x H x D <span>(in)</span>
      </h4>
    </>
  ),
  pump: (
    <>
      <h4 className="w-110">TYPE</h4>
      <h4 className="w-140">MANUFACTURER</h4>
      <h4 className="w-130">MODEL</h4>
      <h4 className="w-80 text-align-right">
        POWER <span>(hp)</span>
      </h4>
      <h4 className="w-94 text-align-right">VOLTAGE</h4>
    </>
  ),
  silencer: (
    <>
      <h4 className="w-160">MANUFACTURER</h4>
      <h4 className="w-130">SERIES</h4>
      <h4 className="w-130">TYPE</h4>
      <h4 className="w-110">SHAPE</h4>
      <h4 className="w-150">FILL MATERIAL</h4>
      <h4 className="w-180">OUTER SKIN</h4>
      <h4 className="w-150">INSIDE LINER</h4>
    </>
  ),
};

/** catalogue_selector_row.tmpl cell sets (non-rate branches — the form-side
 * btn-sync path is never fromRateDialog; the rating dialogs render their own
 * copy of the list). */
const CATALOGUE_CELLS: Record<string, (row: TableRow) => ReactNode> = {
  vfd: (row) => (
    <>
      <span className="w-160">{s(row.mfg_name)}</span>
      <span className="w-150">{s(row.series_name)}</span>
      <span className="w-200">{s(row.model)}</span>
      <DescCell text={s(row.description_name)} />
      <span className="w-120 text-align-right">{s(row.power)}</span>
      <span className="w-110 text-align-right">{s(row.voltage)}</span>
      <span className="w-130 text-align-right">{s(row.fla)}</span>
      <span className="ml-50 w-170">{s(row.nema_encl_type_name)}</span>
    </>
  ),
  damper: (row) => (
    <>
      <span className="w-200">{s(row.mfg_name)}</span>
      <span className="w-100">{s(row.series_name)}</span>
      <span className="w-80 text-align-right">{fixed2(row.aw_width)}</span>
      <span className="w-80 text-align-right">{fixed2(row.aw_height)}</span>
      <span className="ml-40">{s(row.blade_orientation_name)}</span>
    </>
  ),
  louver: (row) => (
    <>
      <span className="w-200">{s(row.mfg_name)}</span>
      <span className="w-100">{s(row.series_name)}</span>
      <span className="w-80 text-align-right">{fixed2(row.aw_width)}</span>
      <span className="w-80 text-align-right">{fixed2(row.aw_height)}</span>
    </>
  ),
  adp_gage: (row) => (
    <>
      <span className="w-200">{s(row.mfg_name)}</span>
      <span className="w-200">{s(row.series_name)}</span>
      <span className="w-100">{s(row.model)}</span>
    </>
  ),
  filter: (row) => (
    <>
      <span className="">{s(row.mfg_name)}</span>
      <span className="w-120">{s(row.series_name)}</span>
      <span className="w-150">{s(row.type_name)}</span>
      <span className="w-40 text-align-right">{s(row.merv_id)}</span>
      <span className="w-65 text-align-right">{truncDec(row.efficiency_value)}</span>
      <span className="w-85 text-align-right">{s(row.depth_nominal)}</span>
    </>
  ),
  frame: (row) => (
    <>
      <span className="w-200">{s(row.mfg_name)}</span>
      <span className="w-200">{s(row.series_name)}</span>
      <span className="w-50">{s(row.mtl_name)}</span>
    </>
  ),
  eliminator: (row) => (
    <>
      <span className="w-200">{s(row.mfg_name)}</span>
      <span className="w-200">{s(row.series_name)}</span>
      <span className="w-100">{s(row.type_name)}</span>
      <span className="w-80 text-align-right">{s(row.depth_nominal)}</span>
    </>
  ),
  light: (row) => (
    <>
      <span className="w-200">{s(row.mfg_name)}</span>
      <span className="w-200 pl-2">{s(row.series)}</span>
      <span className="w-100">{s(row.style_name)}</span>
      <span className="w-80 text-align-right">{s(row.wattage)}</span>
    </>
  ),
  enclosure: (row) => (
    <>
      <span className="w-140">{s(row.mfg_name)}</span>
      <span className="w-70">{s(row.series)}</span>
      <span className="w-310">{s(row.model)}</span>
      <span className="w-70">{s(row.nema_encl_type)}</span>
      <span className="w-100">{whd(row)}</span>
    </>
  ),
  jbox: (row) => (
    <>
      <span className="w-150">{s(row.mfg_name)}</span>
      <span className="w-270">{s(row.model)}</span>
      <span className="w-100">{s(row.nema_encl_type)}</span>
      <span className="w-100">{whd(row)}</span>
    </>
  ),
  disconnect: (row) => (
    <>
      <span className="w-140">{s(row.mfg_name)}</span>
      <span className="w-80 hide">{s(row.series)}</span>
      <span className="w-220">{s(row.model)}</span>
      <span className="w-100 pr-30 text-align-right">{s(row.amp_rating)}</span>
      <span className="w-80">{s(row.nema_encl_type_name)}</span>
      <span className="w-100">{whd(row)}</span>
    </>
  ),
  pump: (row) => (
    <>
      <span className="w-110">{s(row.type_name)}</span>
      <span className="w-140">{s(row.mfg_name)}</span>
      <span className="w-130">{s(row.model)}</span>
      <span className="w-80 text-align-right">{s(row.power)}</span>
      <span className="w-94 text-align-right">{s(row.voltage)}</span>
    </>
  ),
  silencer: (row) => (
    <>
      <span className="w-160">{s(row.mfg_name)}</span>
      <span className="w-130">{s(row.series_name)}</span>
      <span className="w-130">{s(row.type_name)}</span>
      <span className="w-110">{s(row.shape_name)}</span>
      <span className="w-150">{s(row.media_fill_mtl_name)}</span>
      <span className="w-180">{s(row.outside_wall_name)}</span>
      <span className="w-150">{s(row.inside_liner_name)}</span>
    </>
  ),
};

interface TableState {
  kind: string;
  channel: string;
  rows: TableRow[];
  /** Filtered `cms_count` — the pagination bound (`has_available_pages`). */
  count: number | null;
  componentId: number | null;
  accessoryId: number | null;
  /** Sender's `loader.load_page` half; absent = no further pages. */
  loadPage: ((page: number) => Promise<TableRow[]>) | null;
}

/** checkScroll threshold — `const threshold = 1000` (catalogue_selector.js:99). */
const SCROLL_THRESHOLD = 1000;

/**
 * How long the board takes to turn: `.rotate-card { transition: all 0.73s
 * linear }` (styles.css:8800-8807), the ONE transition behind both faces.
 * Exported because the selector dialog has to wait out the smart list's close
 * before starting its own open — see ComponentSelectorDialog.renderFromAHU.
 */
export const ROTATE_CARD_MS = 730;

const CompTable = () => {
  const dispatch = useAppDispatch();
  const [table, setTable] = useState<TableState | null>(null);
  /** `#catalogue-rows-container`, measured by checkScroll. */
  const rowsEl = useRef<HTMLUListElement | null>(null);
  /** `loader.current_page` / `loadingPage` (catalogue_selector.js:139-145). */
  const page = useRef(1);
  const loadingPage = useRef(false);
  /** `self.version` — bumped per open so an in-flight page from the previous
   * one is discarded rather than appended (catalogue_selector.js:31,147-150). */
  const version = useRef(0);

  /**
   * Everything rotateBack does EXCEPT the `.main-rotation` class write and the
   * `close:manual-mode-active` re-emit — i.e. the smart list's own teardown,
   * usable by a caller that has already taken `.main-rotation` over.
   *
   * Legacy's equivalent is the one-liner `self.rotated = false`
   * (catalogue_selector.js:35-37, the `close:manual-mode-active` handler): the
   * Backbone view's markup lives in the DOM permanently and only that flag says
   * whether it is showing. React mounts/unmounts the rows, so dropping `table`
   * IS `rotated = false`, and the sender-side classes (`label.open`,
   * `.btn-sync.active`) have to come off with them or the Smartlist button that
   * opened the list stays lit behind the dialog.
   */
  /** `self.rotated` (catalogue_selector.js:29) — flips false the INSTANT a close
   * starts, while `table` (the rows) is held for the length of the turn. Legacy
   * needs no equivalent split because its markup is permanent; React's rows are
   * mounted, so `table` alone would say "still open" for the whole animation and
   * turn a re-click of the same Smartlist button into a second close. */
  const rotated = useRef(false);
  /** Synchronous mirror of `table` — the bus handlers below are bound once
   * (`[]` deps) and would otherwise read the mount-time state. */
  const tableRef = useRef<TableState | null>(null);

  const teardown = () => {
    tableRef.current = null;
    setTable(null);
    dispatch(setBoardMode({ flipped: false, manualActive: false }));
    document.querySelectorAll('label.open').forEach((el) => el.classList.remove('open'));
    document.querySelectorAll('.btn-sync.active').forEach((el) => el.classList.remove('active'));
    // catalogue_selector.js rotateBack — hide the fixed description tooltip.
    const descTip = document.getElementById('col-desc-fixed-tooltip');
    if (descTip) descTip.style.display = 'none';
  };

  const rotateBack = () => {
    rotated.current = false;
    dispatch(setBoardMode({ flipped: false, manualActive: false }));
    document.querySelectorAll('label.open').forEach((el) => el.classList.remove('open'));
    document.querySelectorAll('.btn-sync.active').forEach((el) => el.classList.remove('active'));
    const descTip = document.getElementById('col-desc-fixed-tooltip');
    if (descTip) descTip.style.display = 'none';
    // Start the turn. ONLY `rotate` comes off here — `manual-mode-active` is
    // what keeps `.comp-table` displayed (styles.css:8830), and legacy holds it
    // for the whole animation and drops it in a trailing timer guarded by
    // `if (!self.rotated)` (catalogue_selector.js:281-285). React dropped both
    // classes in the same statement AND unmounted the rows, so the back face
    // went blank on the first frame and the list appeared to vanish rather than
    // turn away — `backface-visibility:hidden` on `.main-section` means there is
    // nothing else to see until the card passes 90°.
    document.querySelector('.main-rotation')?.classList.remove('rotate');
    window.setTimeout(() => {
      // Guarded exactly like legacy: a re-open during the turn owns the face now.
      if (rotated.current) return;
      document.querySelector('.main-rotation')?.classList.remove('manual-mode-active');
      teardown();
      document.querySelector('.toolbar-ctrl-labels')?.classList.remove('hide');
      ahuBoardBus.emit('close:manual-mode-active');
    }, ROTATE_CARD_MS);
  };

  const rotateOpen = (next: TableState) => {
    rotated.current = true;
    dispatch(setBoardMode({ flipped: true, manualActive: true }));
    const rotation = document.querySelector('.main-rotation');
    rotation?.classList.add('rotate', 'manual-mode-active');
    document.querySelector('.toolbar-ctrl-labels')?.classList.add('hide');
    // Mark the owning Data Bar row. Lives here rather than at the top of the
    // channel handler so it lands WITH the list: when the list is waiting out a
    // dialog's close, marking on the click would darken the row while the dialog
    // is still on screen.
    markOwnerRow(next.kind, next.componentId);
    // The sender hands over page 1 (`load()` on rotateOpen); the scroll handler
    // resumes from there.
    page.current = 1;
    loadingPage.current = false;
    version.current += 1;
    tableRef.current = next;
    setTable(next);
  };

  useEffect(() => {
    const offs = TABLE_KINDS.map((kind) =>
      ahuBoardBus.on(`rotate:canvas:${kind}:table`, (...args: unknown[]) => {
        const options = (args[0] ?? {}) as Record<string, unknown>;
        const name = String(options.name ?? '');
        const channel = channelFor(name, kind);
        const prev = tableRef.current;
        // Re-click of the same table closes (§11.1). `rotated` and not `prev` is
        // the open test: the rows outlive a close by one turn so the card has
        // something to show while it turns away, and legacy's own gate is
        // `self.rotated`, cleared the moment rotateBack starts.
        if (rotated.current && prev && prev.kind === kind && prev.channel === channel) {
          rotateBack();
          return;
        }
        const collection = options.collection;
        const rows: TableRow[] = Array.isArray(collection)
          ? (collection as TableRow[])
          : Array.isArray((collection as { models?: unknown })?.models)
            ? ((collection as { models: TableRow[] }).models)
            : [];
        const next: TableState = {
          kind,
          channel,
          rows,
          count: options.count != null ? Number(options.count) : null,
          // The Data Bar row this list belongs to — markOwnerRow (called from
          // rotateOpen) marks it, so it lands with the list rather than at the
          // start of a handover the list is still waiting out.
          componentId: options.component_id != null ? Number(options.component_id) : null,
          accessoryId: options.accessory_id != null ? Number(options.accessory_id) : null,
          loadPage:
            typeof options.loadPage === 'function'
              ? (options.loadPage as (p: number) => Promise<TableRow[]>)
              : null,
        };

        // ★ THE DIALOG OWNS THE FACE — let it turn away first.
        //
        // catalogue_selector.js rotateOpen:209-214 does the handover in ONE
        // tick: `$('.main-rotation').attr('class', 'main-rotation')` +
        // `rating_mode:hide`, then straight on to add `rotate` again. `.rotate`
        // is set before and after, so on dev the card never turns — the dialog
        // is simply replaced by the list between two frames. QA asked for the
        // two movements to be separate, so the close is handed to the chassis
        // (`rating_mode:hide` — the same close its own CLOSE button runs) and
        // the open waits out the turn.
        //
        // `rating-mode-active` is the dialog's marker and it is now held for
        // exactly that turn (ComponentSelectorDialog.hideRatingDialogBox), so
        // its presence is the test for "the dialog is on the face right now".
        if (document.querySelector('.main-rotation')?.classList.contains('rating-mode-active')) {
          ahuBoardBus.emit('rating_mode:hide');
          window.setTimeout(() => {
            // The close leaves `main-rotation` bare; belt-and-braces in case one
            // of its own timers has not landed yet, so the open always starts
            // from a settled, canvas-side face.
            const face = document.querySelector('.main-rotation');
            if (face) face.className = 'main-rotation';
            rotateOpen(next);
          }, ROTATE_CARD_MS);
          return;
        }
        rotateOpen(next);
      })
    );
    offs.push(
      ahuBoardBus.on('rotate:canvas', () => {
        rotateBack();
      })
    );
    // catalogue_selector.js:35-37 — a FORCE close by whoever else wants the
    // flipped face. The dialog chassis fires this after resetting
    // `.main-rotation` itself (tabs.navy.panel.js showRating:145-146), so this
    // must NOT write those classes back or re-emit; it only drops the list.
    // rotateBack's own trailing emit lands here too and is a no-op by then.
    offs.push(
      ahuBoardBus.on('close:manual-mode-active', () => {
        teardown();
        document.querySelector('.toolbar-ctrl-labels')?.classList.remove('hide');
      })
    );
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * `checkScroll` (catalogue_selector.js:97-119): while within `threshold` of
   * the bottom and `has_available_pages()` holds (loaded_count < cms_count),
   * load the next page and re-measure — one scroll pulls several pages when a
   * page does not fill the remaining gap, which is legacy's recursion.
   */
  const checkScroll = useCallback(async (open: TableState) => {
    if (loadingPage.current || !open.loadPage) return;
    loadingPage.current = true;
    const opened = version.current;
    try {
      let loaded = open.rows.length;
      // Backbone keys a collection by id, so `add` silently drops a repeat.
      // That matters here: several of these list endpoints (silencers,
      // filters, eliminators) are NOT paginated and hand back the whole
      // filtered set whatever `?page=` says, so a page 2 would otherwise
      // append the same rows again.
      const seen = new Set(open.rows.map((r) => String(r.id)));
      // Bounded so an endpoint that keeps returning rows past cms_count (or
      // reports no count at all) cannot spin: 50 pages is far beyond any real
      // catalogue.
      for (let guard = 0; guard < 50; guard += 1) {
        const el = rowsEl.current;
        if (!el || version.current !== opened) return;
        if (open.count != null && loaded >= open.count) return;
        if (el.scrollHeight - (el.scrollTop + el.clientHeight) >= SCROLL_THRESHOLD) return;
        const page_ = await open.loadPage(page.current + 1);
        // a close/re-open mid-flight owns the list now — drop the page
        if (version.current !== opened) return;
        const next = page_.filter((r) => !seen.has(String(r.id)));
        if (!next.length) return;
        next.forEach((r) => seen.add(String(r.id)));
        page.current += 1;
        loaded += next.length;
        setTable((prev) => (prev ? { ...prev, rows: [...prev.rows, ...next] } : prev));
        // let the appended rows lay out before re-measuring the gap
        await new Promise((r) => window.requestAnimationFrame(r));
      }
    } catch {
      /* 'Previous request canceled.' — legacy swallows it too */
    } finally {
      loadingPage.current = false;
    }
  }, []);

  if (!table) return null;

  const onRowClick = (row: TableRow) => {
    ahuBoardBus.emit(`choose:${table.channel}:master`, {
      id: row.id,
      component_id: table.componentId ?? undefined,
      accessory_id: table.accessoryId ?? undefined,
    });
    rotateBack();
  };

  // catalogue_selector.tmpl: smart-list-wrap chassis, the componentName's
  // column set when the template has one, SPN hidden for vfd/silencer, text
  // CLOSE control (`close-button-li larger`, :95-100).
  const headings = CATALOGUE_HEADINGS[table.kind];
  const cells = CATALOGUE_CELLS[table.kind];
  const spnHidden = table.kind === 'vfd' || table.kind === 'silencer';
  return (
    <div
      className={`smart-list-wrap${headings ? ' min-width-1000' : ''}${
        table.kind === 'enclosure' ? ' min-width-1250' : ''
      }`}
    >
      <ul className="list-heading list-inline" id="smart-list-ul">
        {!spnHidden && (
          <li className="spn-row">
            <h4>SIGNIFICANT PART NUMBER</h4>
          </li>
        )}
        {headings && <li className={`only-for-${table.kind} custom-columns`}>{headings}</li>}
        <li className="close-button-li larger">
          <label />
          <button className="close" onClick={rotateBack}>
            CLOSE
          </button>
        </li>
      </ul>
      <ul
        id="catalogue-rows-container"
        className="list-rows scrollable"
        ref={rowsEl}
        onScroll={() => void checkScroll(table)}
      >
        {table.rows.map((row) => (
          <li key={String(row.id)} onClick={() => onRowClick(row)}>
            <ul {...{ name: String(row.id) }} className={`table-item ${table.kind}`}>
              <li className="xxlong non-clickable text-overflow-unset">
                {!spnHidden && (
                  <span className="spn-row">
                    {/* catalogue_selector_row.tmpl:6 — coil rows carry the
                        nipple and valve spns beside the part number. */}
                    {String(row.spn ?? '')}
                    {row.nipple ? <span>{String(row.nipple)}</span> : null}
                    {row.float_valve_spn ? (
                      <>
                        {' | '}
                        <span>{String(row.float_valve_spn)}</span>
                      </>
                    ) : null}
                    {row.automatic_valve_spn ? (
                      <>
                        {' | '}
                        <span>{String(row.automatic_valve_spn)}</span>
                      </>
                    ) : null}
                  </span>
                )}
                {cells && (
                  <span className={`only-for-${table.kind}-row custom-columns-row`}>
                    {cells(row)}
                  </span>
                )}
              </li>
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CompTable;
